// src/pages/supplier/AnalyticsPage.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supplierAnalyticsApi } from '../../api/services'
import { Card, CardHeader, CardBody, PageSpinner, StatCard } from '../../components/common/ui'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { TrendingUp, Package, Star } from 'lucide-react'

export default function AnalyticsPage() {
  const [days, setDays] = useState(30)
  const { data, isLoading } = useQuery({ queryKey: ['supplier-analytics', days], queryFn: () => supplierAnalyticsApi.index({ days }) })
  if (isLoading) return <PageSpinner />
  const { kpis = {}, revenue_chart = [], top_products = [] } = data?.data?.data || {}

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-display font-bold">Analytics</h1>
        <div className="flex gap-1">
          {[7,30,90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${days === d ? 'bg-pink text-white border-pink' : 'border-border text-muted hover:border-pink'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<TrendingUp size={18} />} label="Revenue" value={`₹${Number(kpis.gmv || 0).toLocaleString('en-IN')}`} />
        <StatCard icon={<Package size={18} />} label="Orders" value={kpis.orders ?? 0} />
        <StatCard icon={<Star size={18} />} label="Avg Rating" value={kpis.avg_rating ? Number(kpis.avg_rating).toFixed(1) + ' ★' : '—'} />
        <StatCard label="Avg Order Value" value={`₹${Number(kpis.avg_order_value || 0).toLocaleString('en-IN')}`} />
      </div>

      {revenue_chart.length > 0 && (
        <Card>
          <CardHeader><span className="font-bold text-sm">Revenue Trend</span></CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={revenue_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue']} />
                <Line type="monotone" dataKey="revenue" stroke="#FF3D7F" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      {top_products.length > 0 && (
        <Card>
          <CardHeader><span className="font-bold text-sm">Top Products</span></CardHeader>
          <CardBody className="p-0">
            {top_products.slice(0, 5).map((p, i) => (
              <div key={p.product_id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-b-0">
                <span className="text-sm font-medium">{p.product_name}</span>
                <span className="text-sm font-bold">₹{Number(p.revenue).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  )
}
